import React, { useCallback } from "react";
import PropTypes from "prop-types";

import { first, get, isEmpty } from "lodash";

import BannerImagePicker, {
  IMAGE_SOURCE_TYPES,
} from "src/components/BannerImagePicker";
import {
  getSourceTypeKey,
  upsertAttribute,
  resetAttributes,
  replaceImageWidth,
  getCropDataAttributeType,
  isCroppableAttributeType,
  getLayoutModeFromAttributeType,
} from "src/components/BannerImagePicker/BannerImagePicker.util";
import { ENTITY_ATTRIBUTE_TYPE } from "src/consts/entityAttribute";
import {
  RESET_TO_DEFAULT,
  getLayoutConfig,
} from "src/components/BannerImagePicker/consts";
import { useGetDefaultBackgroundItem } from "src/components/BackgroundImageContainer/hooks/useGetDefaultBackgroundItem";
import { useUpdateBackground } from "src/components/BackgroundImageContainer/hooks/useUpdateBackground";

const BackgroundImageContainer = ({
  entityType,
  entityId,
  attributes,
  isVisible,
  onHideModal,
  resourceTrackingType,
  attributeType = ENTITY_ATTRIBUTE_TYPE.BANNER_IMAGE,
  disabled = false,
  initialValue = null,
  allowedSources,
  onAttributesUpdated,
}) => {
  const defaultBackgroundItem = useGetDefaultBackgroundItem({
    attributes,
    attributeType,
  });
  const cropDataAttributeType = getCropDataAttributeType(attributeType);

  // Determine layout mode and get configuration
  const layoutMode = getLayoutModeFromAttributeType(attributeType);
  const layoutConfig = getLayoutConfig(layoutMode);

  const updateAttributes = useUpdateBackground({
    entityType,
    entityId,
  });

  const handleRemoveBg = useCallback(async () => {
    const newAttributes = resetAttributes({
      attributes,
      attributeTypes: [attributeType, cropDataAttributeType],
    });
    if (!isEmpty(newAttributes)) {
      const result = await updateAttributes(newAttributes);
      if (onAttributesUpdated && result) {
        onAttributesUpdated(result);
      }
    }
  }, [
    attributeType,
    attributes,
    cropDataAttributeType,
    updateAttributes,
    onAttributesUpdated,
  ]);

  const handleUpdateBackgroundImage = useCallback(
    async (selection, selectedSourceType) => {
      if (disabled || !entityId) {
        return false;
      }

      let newAttributes;

      if (selectedSourceType === IMAGE_SOURCE_TYPES.COLOR_PICKER) {
        newAttributes = upsertAttribute({
          attributes,
          value: get(selection, "color"),
          label: get(selection, "name"),
          attributeType,
          subtype: selectedSourceType,
        });
      } else {
        const { imgUrl, altKey } = getSourceTypeKey(selectedSourceType);
        newAttributes = upsertAttribute({
          attributes,
          value: replaceImageWidth(
            get(selection, imgUrl),
            layoutConfig.maxImageWidth
          ),
          label: get(selection, altKey),
          attributeType,
          subtype: selectedSourceType,
        });
      }

      if (isCroppableAttributeType(attributeType, selectedSourceType)) {
        const cropData = upsertAttribute({
          attributes,
          value: selection?.cropData
            ? JSON.stringify(selection?.cropData)
            : null,
          label: "Image Crop Data",
          attributeType: cropDataAttributeType,
          subtype: selectedSourceType,
        });

        if (!isEmpty(cropData)) {
          newAttributes.push(first(cropData));
        }
      }
      const result = await updateAttributes(newAttributes);
      if (onAttributesUpdated && result) {
        onAttributesUpdated(result);
      }
      return result;
    },
    [
      disabled,
      entityId,
      attributeType,
      updateAttributes,
      attributes,
      layoutConfig.maxImageWidth,
      cropDataAttributeType,
      onAttributesUpdated,
    ]
  );

  const onUpdate = useCallback(
    async (selection, selectedSourceType) => {
      if (selection?.type === RESET_TO_DEFAULT) {
        await handleRemoveBg();
      } else {
        await handleUpdateBackgroundImage(selection, selectedSourceType);
      }
    },
    [handleRemoveBg, handleUpdateBackgroundImage]
  );

  return (
    <BannerImagePicker
      attributes={attributes}
      onUpdate={onUpdate}
      isVisible={isVisible}
      onHideModal={onHideModal}
      resourceTrackingType={resourceTrackingType}
      initialValue={initialValue}
      onRemoveBg={handleRemoveBg}
      defaultBackgroundItem={defaultBackgroundItem}
      allowedSources={allowedSources}
      layoutMode={layoutMode}
      layoutConfig={layoutConfig}
    />
  );
};

BackgroundImageContainer.propTypes = {
  entityType: PropTypes.oneOf(["person", "team", "tag"]).isRequired,
  entityId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  attributeType: PropTypes.string,
  attributes: PropTypes.arrayOf(PropTypes.object),
  isVisible: PropTypes.bool,
  onHideModal: PropTypes.func,
  resourceTrackingType: PropTypes.string,
  initialValue: PropTypes.object,
  allowedSources: PropTypes.arrayOf(PropTypes.string),
  onAttributesUpdated: PropTypes.func,
};

export default BackgroundImageContainer;
